import React, { Suspense, useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Panel } from 'react-bootstrap';
import _ from 'lodash';

import withAuthentication from '../../hoc/withAuthentication';
import withLayout from '../../hoc/withLayout';

import { useAlertStore } from '../../store/providers/Alert';
import { useFranchiseRolesStore } from '../../store/providers/FranchiseRoles';
import Permission from '../../components/Permission/Permission';

import { breadcrumbAddRoute } from '../../store/actions/breadcrumb';
import Spinner from '../../components/Spinner/Spinner';
import * as constants from '../../constants';
import { columns, sorting } from './FranchiseRolesSchema';
import { navigate } from 'raviger';
import { modifyToInstance } from '../../lib/Formatter';

const BaseList = React.lazy(() => import('../../components/Lists/BaseList/BaseList'));
const SideMenu = React.lazy(() => import('../../components/SideMenu/SideMenu'));
const PageHeader = React.lazy(() => import('../../components/PageHeader/PageHeader'));
const FranchiseRolesModal = React.lazy(() => import('./FranchiseRolesModel'));

function FranchiseRoles() {
    const dispatch = useDispatch();

    const { addAlert } = useAlertStore();
    const { listRecords, newRecord, data, clearData } = useFranchiseRolesStore();

    const [isMounted, setIsMounted] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [listData, setListData] = useState([]);
    const [selectedRoleId] = useState();

    const pagePermission = constants.PERMISSION_SETTINGS_FRANCHISEROLEMANAGEMENT;
    const [hasPermission, setHasPermission] = useState(false);
    const [canEdit, setCanEdit] = useState(false);


    const viewPermissionsHandler = (role) => {
        navigate(`/userroles/edit/roles/${role.id}`);
    }

    const addedHandler = () => {
        setIsEditing(true);
        newRecord();
    };

    const refreshedHandler = useCallback(() => {
        listRecords({ role: 'fm', status: 1 });
    }, [listRecords]);

    const cancelledHandler = () => {
        setIsEditing(false);
    };

    const submittedHandler = () => {
        setIsEditing(false);
        refreshedHandler();
    };

    useEffect(() => {
        if (!_.isEmpty(data)) {
            if (data.status === 'fail' || data.status === 'error') {
                setIsEditing(false);
                addAlert({ key: constants.ALERT_SERVER_ERROR, text: <strong>{data.body}</strong>, type: constants.ALERT_TYPE_DANGER });
                clearData();
            }

            if (data.scope === 'delete' && data.status === 'success') { refreshedHandler(); }
            if (data.status === 'success' && data.scope === 'list') {
                let result = data.body;
                if (Boolean(result && result.length)) {
                    result = result.map(record => {
                        if (record && record.name) {
                            record.name = modifyToInstance(record.name);
                        }
                        return { ...record }
                    })
                }
                setListData(result);
            }
            setIsBusy(data.status === 'processing' && (data.scope === 'delete' || data.scope === 'list'));
        }
    }, [data, addAlert, clearData, refreshedHandler]);

    useEffect(() => {
        if (!hasPermission) { return; }
        if (isMounted) { return; }

        dispatch(breadcrumbAddRoute({ key: 'franchise-roles', text: 'User Roles', path: '/userroles' }));
        refreshedHandler();

        setIsMounted(true);
    }, [isMounted, hasPermission, refreshedHandler, dispatch]);

    return (
        <>
            <SideMenu activeKey="settings" />
            <div className="content-body">
                <Permission showDenied={true} pagePermission={pagePermission} hasPermission={hasPermission} setHasPermission={setHasPermission} setCanEdit={setCanEdit} />
                {hasPermission && isMounted &&
                    <Suspense fallback={<Spinner text="Loading..." />}>
                        {isEditing &&
                            <FranchiseRolesModal
                                canEdit={canEdit}
                                show={isEditing}
                                roleId={selectedRoleId}
                                title={`New User Role`}
                                cancelled={() => cancelledHandler()}
                                submitted={() => submittedHandler()}
                            />
                        }
                        <div style={{ paddingRight: '15px' }}>
                            <PageHeader title="User Roles" />
                            <Panel>
                                <Panel.Body>
                                    <BaseList
                                        hideFilter={false}
                                        compType="Role"
                                        keyField="id"
                                        listData={listData}
                                        activeValue={true}
                                        activeField="isActive"
                                        hidePagination={true}
                                        isBusy={isBusy}
                                        sorting={sorting}
                                        columns={columns}
                                        hideCopy={true}
                                        hidePrint={true}
                                        hideDelete={true}
                                        permissionEdit={canEdit}
                                        added={() => addedHandler()}
                                        selected={(role) => viewPermissionsHandler(role)}
                                        refreshed={() => refreshedHandler()} />
                                </Panel.Body>
                            </Panel>
                        </div>
                    </Suspense>
                }
            </div>
        </>
    );
}

export default withAuthentication(withLayout(FranchiseRoles));